import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { cn } from "@/lib/utils";

export type CounterpartyStatus =
  | "WAITING_PAYMENT"
  | "PAYMENT_RECEIVED"
  | "BILLING_REQUESTED"
  | "BILLING_DONE"
  | "PAYMENT_SENT"
  | "CANCELED";

interface StatusOption {
  value: CounterpartyStatus;
  label: string;
  dotClass: string;
  textClass: string;
}

/** Port of ui/select/CounterpartyStatusSelect.vue — colored status pill dropdown. */
const STATUS_OPTIONS: StatusOption[] = [
  { value: "WAITING_PAYMENT", label: "En attente", dotClass: "bg-violet-500", textClass: "text-violet-700" },
  { value: "PAYMENT_RECEIVED", label: "Reçu", dotClass: "bg-emerald-500", textClass: "text-emerald-700" },
  {
    value: "BILLING_REQUESTED",
    label: "Demande de facture",
    dotClass: "bg-amber-500",
    textClass: "text-amber-700",
  },
  { value: "BILLING_DONE", label: "Facturation reçue", dotClass: "bg-teal-500", textClass: "text-teal-700" },
  { value: "PAYMENT_SENT", label: "Paiement envoyé", dotClass: "bg-lime-500", textClass: "text-lime-700" },
  { value: "CANCELED", label: "Annulé", dotClass: "bg-gray-400", textClass: "text-gray-600" },
];

interface CounterpartyStatusSelectProps {
  value: CounterpartyStatus;
  disabled?: boolean;
  onChange: (status: CounterpartyStatus) => void;
}

export function CounterpartyStatusSelect({ value, disabled, onChange }: CounterpartyStatusSelectProps) {
  const selected = STATUS_OPTIONS.find((option) => option.value === value) ?? STATUS_OPTIONS[0];

  return (
    <Select
      value={value}
      disabled={disabled}
      onValueChange={(next) => {
        if (typeof next === "string" && next !== value) {
          onChange(next as CounterpartyStatus);
        }
      }}
    >
      <SelectTrigger
        className={cn(
          "h-7 w-fit min-w-0 rounded-full border-gray-200 bg-white px-2.5 text-xs font-medium",
          selected.textClass,
        )}
        size="sm"
      >
        <span className="flex items-center gap-2 truncate">
          <span className={cn("h-2 w-2 flex-shrink-0 rounded-full", selected.dotClass)} />
          {selected.label}
        </span>
      </SelectTrigger>
      <SelectContent>
        {STATUS_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            <span className="flex items-center gap-2">
              <span className={cn("h-2 w-2 flex-shrink-0 rounded-full", option.dotClass)} />
              <span className={option.textClass}>{option.label}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
